import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type { Select } from 'ember-power-select/components/power-select';
import type { PowerSelectTriggerSignature } from 'ember-power-select/components/power-select/trigger';
import { and, not } from 'ember-truth-helpers';

import type {
  Group,
  Option,
} from '../../../types/components/prism/combo-select-input.ts';
import PrismBadge from '../badge.gts';
import PrismIcon from '../icon.gts';
import PrismIconFlag from '../icon-flag.gts';

interface PrismComboSelectInputTriggerArgs {
  isSecondary?: true;
  isSmall?: true;
  placeholder?: string;
  unsafe_isClampedToOneLine?: true;
  unsafe_isClearable?: boolean;
}

interface PrismComboSelectInputTriggerSignature {
  Args: PowerSelectTriggerSignature<Option, Group<Option>>['Args'] &
    PrismComboSelectInputTriggerArgs;
}

export default class PrismComboSelectInputTrigger extends Component<PrismComboSelectInputTriggerSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get labelFontClass() {
    if (this.args.isSecondary) {
      return this.args.isSmall ? 'text-200--regular' : 'text-300--regular';
    }

    return this.args.isSmall ? 'text-200--medium' : 'text-300--medium';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get select() {
    return this.args.select as Select<Option, Group<Option>>;
  }

  // related to unsafe_isClearable, copied from https://github.com/cibernox/ember-power-select/blob/ae707d0e4a0baf8477d2629a51a27cee00753648/addon/components/power-select/trigger.ts#L8-L15
  @action
  unsafe_clear(e: Event): false | void {
    e.stopPropagation();
    this.select.actions.select(null);
    if (e.type === 'touchstart') {
      return false;
    }
  }

  <template>
    {{! template-lint-disable no-pointer-down-event-binding }}
    {{#if this.select.selected.icon}}
      <PrismIcon
        @color="inherit"
        @name={{this.select.selected.icon}}
        @size="small"
      />
    {{/if}}
    {{#if this.select.selected.iconFlag}}
      <PrismIconFlag @code={{this.select.selected.iconFlag}} @size="small" />
    {{/if}}
    {{#if @select.selected}}
      <p
        class="trigger__label
          {{this.labelFontClass}}
          {{if @unsafe_isClampedToOneLine 'truncate'}}"
      >
        {{this.select.selected.label}}
      </p>
      {{! Clear button }}
      {{#if (and @unsafe_isClearable (not this.select.disabled))}}
        <button
          class="prism-button--unstyled"
          type="button"
          {{on "mousedown" this.unsafe_clear}}
        >
          <PrismIcon @name="cross" @size="small" />
        </button>
      {{/if}}
    {{else}}
      <p
        class="trigger__placeholder
          {{this.labelFontClass}}
          {{if @unsafe_isClampedToOneLine 'truncate'}}"
      >
        {{@placeholder}}
      </p>
    {{/if}}

    {{#if this.select.selected.badge}}
      <PrismBadge
        @badgeType={{if
          this.select.selected.badgeType
          this.select.selected.badgeType
          "info"
        }}
      >{{this.select.selected.badge}}</PrismBadge>
    {{/if}}

    {{#if @select.disabled}}
      <PrismIcon @color="disabled" @name="chevron-down" @size="small" />
    {{else}}
      {{#if @select.isOpen}}
        <PrismIcon @name="chevron-up" @size="small" />
      {{else}}
        <PrismIcon @name="chevron-down" @size="small" />
      {{/if}}
    {{/if}}
  </template>
}
